// src/crm/audience/audienceFilter.ts
// -----------------------------------
// PURE audience-resolution logic for the CRM module: given raw shelter + contact
// rows (from the existing shelters/profiles repos), a suppression list, manual
// exclusions, and a filter set, produce the de-duplicated recipient list for a
// broadcast. No I/O here -- the repo fetches the rows, this module decides who
// gets the email -- so the rules are exhaustively unit-testable (see
// audienceFilter.test.ts). Resolution runs at SEND time (decision O-1), excluding
// anyone on the suppression list at that moment.

import { isValidEmail, normalizeEmail } from "../../utils/sanitize";

// normalizeEmail now lives in the shared utils/sanitize (email-validation-plan §5);
// re-exported so existing imports from this module keep working.
export { normalizeEmail };

export type AudienceMode = "shelters_primary" | "all_contacts";

export interface AudienceFilters {
  state?: string | null; // legacy single state (Feedback/Telemarketing before Stage 2, old saved filters)
  states?: string[] | null; // Stage 2 tick boxes: [] / null = all states; wins over `state` when non-empty
  customerType?: string | null; // a customer-type CATEGORY (db-124), matched vs shelter.customer_category
  salesGroup?: string | null;
  onboardedFrom?: string | null; // ISO yyyy-mm-dd (inclusive)
  onboardedTo?: string | null; // ISO yyyy-mm-dd (inclusive)
  activeOnly?: boolean; // default true: exclude deactivated
  excludeTestCodes?: boolean; // default true: exclude shelter codes starting with TEST
  search?: string | null; // case-insensitive substring
  stockHeld?: string | null; // "" = all · "yes" = holds stock · "no" = the rest
}

export interface ShelterLike {
  shelter_code: string;
  shelter_name?: string | null;
  email?: string | null;
  state?: string | null;
  customer_type?: string | null;
  customer_category?: string | null; // db-124 broad grouping; the "Customer type" filter matches THIS
  sales_group?: string | null;
  date_onboarded?: string | null;
  deactivated?: boolean | null;
  stock_held?: boolean | null; // db column; the "Stock Held" filter matches THIS
}

// Contact-model Batch 4: a CRM contact is now a `user_shelters` membership (per person
// × service). `email`/`display_name` come from the membership; `isPrimary` is derived
// from the shelters.primary_contact pointer (not the old primary_contact_label);
// `deactivated` reflects the login user's account (login-less contacts are always active).
export interface ContactLike {
  email?: string | null;
  display_name?: string | null;
  shelter_code?: string | null;
  isPrimary?: boolean | null;
  deactivated?: boolean | null;
}

export interface Recipient {
  email: string;
  name: string;
  shelterCode: string | null;
}

export interface Skipped {
  shelterCode: string | null;
  name: string;
  reason: string;
}

export interface ResolveInput {
  mode: AudienceMode;
  shelters: ShelterLike[];
  contacts: ContactLike[];
  unsubscribed?: string[]; // emails (any case)
  excluded?: string[]; // manually excluded emails (any case)
  filters?: AudienceFilters;
}

export interface ResolveResult {
  recipients: Recipient[];
  skipped: Skipped[]; // shown in the preview ("3 skipped - no contact email")
}

// Test/dummy services carry a shelter_code beginning "TEST" (case-insensitive, e.g.
// TEST001, test-qld). The audience drops them by default (filters.excludeTestCodes,
// default true). A null/absent code is never a TEST code, so login-less contacts pass.
export function isTestCode(code: string | null | undefined): boolean {
  return (code ?? "").toUpperCase().startsWith("TEST");
}

function includesCI(haystack: string | null | undefined, needle: string): boolean {
  return (haystack ?? "").toLowerCase().includes(needle);
}

// Stock-held filter (John, 5 Sep 2026; shared into the CRM Audience 6 Sep 2026):
// "yes" = the service holds stock; "no" = it doesn't (false OR unset). An absent
// shelter counts as not holding stock. "" / null / undefined = don't filter.
function matchesStockHeld(
  shelter: ShelterLike | undefined,
  stockHeld: string | null | undefined
): boolean {
  if (stockHeld !== "yes" && stockHeld !== "no") return true;
  const held = shelter?.stock_held === true;
  return stockHeld === "yes" ? held : !held;
}

// The effective state filter (Stage 2, 30 Sep 2026): the ticked list when it has
// entries, else the legacy single `state`, else null (= every state). Keeping the
// single-state path means callers and saved filters that pass `state` behave exactly
// as before.
function selectedStates(filters: AudienceFilters): string[] | null {
  if (filters.states && filters.states.length > 0) return filters.states;
  if (filters.state) return [filters.state];
  return null;
}

// Does a shelter pass the (shelter-scoped) filters? Used directly for mode A and
// as the enrichment/eligibility check for a contact's shelter in mode B.
export function shelterPassesFilters(
  shelter: ShelterLike | undefined,
  filters: AudienceFilters
): boolean {
  if (!shelter) {
    // A contact with no matching shelter only passes when no shelter-scoped
    // filter is active (state/type/group/onboarded/stockHeld). Search is handled
    // by caller. stockHeld="no" is compatible with no shelter (not held); "yes"
    // is not (an absent shelter can't hold stock).
    if (!matchesStockHeld(shelter, filters.stockHeld)) return false;
    return !(
      selectedStates(filters) ||
      filters.customerType ||
      filters.salesGroup ||
      filters.onboardedFrom ||
      filters.onboardedTo
    );
  }
  const activeOnly = filters.activeOnly !== false;
  if (activeOnly && shelter.deactivated) return false;
  if (!matchesStockHeld(shelter, filters.stockHeld)) return false;
  const states = selectedStates(filters);
  if (states && !states.includes(shelter.state ?? "")) return false;
  // The "Customer type" filter selects a broad CATEGORY (db-124 option_category),
  // not the raw customer_type value; the caller enriches each shelter with its
  // customer_category. (Field name kept as customerType across the filter plumbing.)
  if (filters.customerType && (shelter.customer_category ?? "") !== filters.customerType)
    return false;
  if (filters.salesGroup && shelter.sales_group !== filters.salesGroup) return false;
  if (filters.onboardedFrom || filters.onboardedTo) {
    const d = shelter.date_onboarded ?? "";
    if (!d) return false;
    if (filters.onboardedFrom && d < filters.onboardedFrom) return false;
    if (filters.onboardedTo && d > filters.onboardedTo) return false;
  }
  return true;
}

function buildShelterMap(shelters: ShelterLike[]): Map<string, ShelterLike> {
  const m = new Map<string, ShelterLike>();
  for (const s of shelters) {
    if (s.shelter_code) m.set(s.shelter_code, s);
  }
  return m;
}

// The primary contact for a shelter: the active membership flagged isPrimary (from the
// shelters.primary_contact pointer) that has an email. Returns undefined if none.
function findPrimaryContact(
  contacts: ContactLike[],
  shelterCode: string,
  activeOnly: boolean
): ContactLike | undefined {
  return contacts.find(
    (ct) =>
      ct.shelter_code === shelterCode &&
      !!ct.isPrimary &&
      !!normalizeEmail(ct.email) &&
      !(activeOnly && ct.deactivated)
  );
}

export function resolveAudience(input: ResolveInput): ResolveResult {
  const filters: AudienceFilters = input.filters ?? {};
  const activeOnly = filters.activeOnly !== false;
  const excludeTest = filters.excludeTestCodes !== false;
  const search = (filters.search ?? "").trim().toLowerCase();
  const unsub = new Set((input.unsubscribed ?? []).map(normalizeEmail).filter(Boolean));
  const excluded = new Set((input.excluded ?? []).map(normalizeEmail).filter(Boolean));
  const shelterMap = buildShelterMap(input.shelters);

  const recipients: Recipient[] = [];
  const skipped: Skipped[] = [];
  const seen = new Set<string>();

  const push = (email: string, name: string, shelterCode: string | null): void => {
    const key = normalizeEmail(email);
    if (!key) return;
    if (excluded.has(key)) return; // manual exclusion: drop silently
    if (unsub.has(key)) {
      skipped.push({ shelterCode, name, reason: "unsubscribed" });
      return;
    }
    // Caught here, in the preview, so an unsendable address never reaches a queued
    // campaign (the 15/09/2026 trailing-space failure). email-validation-plan §6D.
    if (!isValidEmail(key)) {
      skipped.push({ shelterCode, name, reason: "invalid email" });
      return;
    }
    if (seen.has(key)) return; // de-dupe by email (first wins)
    seen.add(key);
    // Store the NORMALISED address (trimmed + lowercased), never the raw source value.
    recipients.push({ email: key, name, shelterCode });
  };

  if (input.mode === "shelters_primary") {
    for (const shelter of input.shelters) {
      if (!shelterPassesFilters(shelter, filters)) continue;
      if (excludeTest && isTestCode(shelter.shelter_code)) continue;
      if (
        search &&
        !(includesCI(shelter.shelter_name, search) || includesCI(shelter.shelter_code, search))
      )
        continue;
      const contact = findPrimaryContact(input.contacts, shelter.shelter_code, activeOnly);
      const email = normalizeEmail(contact?.email) || normalizeEmail(shelter.email);
      const name = contact?.display_name || shelter.shelter_name || email;
      if (!email) {
        skipped.push({
          shelterCode: shelter.shelter_code,
          name: shelter.shelter_name || shelter.shelter_code,
          reason: "no contact email",
        });
        continue;
      }
      push(email, name, shelter.shelter_code);
    }
    return { recipients, skipped };
  }

  // all_contacts
  for (const ct of input.contacts) {
    if (activeOnly && ct.deactivated) continue;
    if (excludeTest && isTestCode(ct.shelter_code)) continue;
    const shelter = ct.shelter_code ? shelterMap.get(ct.shelter_code) : undefined;
    if (!shelterPassesFilters(shelter, filters)) continue;
    if (
      search &&
      !(
        includesCI(ct.display_name, search) ||
        includesCI(ct.email, search) ||
        includesCI(shelter?.shelter_name, search) ||
        includesCI(ct.shelter_code, search)
      )
    )
      continue;
    const email = normalizeEmail(ct.email);
    const name = ct.display_name || ct.email || ct.shelter_code || "";
    if (!email) {
      skipped.push({
        shelterCode: ct.shelter_code ?? null,
        name: name || "(no name)",
        reason: "no email",
      });
      continue;
    }
    push(email, name, ct.shelter_code ?? null);
  }
  return { recipients, skipped };
}
